/**
 * ProductPage Component
 * Individual product detail page
 */

import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Layout } from '~/components/layout/Layout';
import { ProductDetails } from '~/components/product/ProductDetails';
import { ProductGrid } from '~/components/product/ProductGrid';
import { getProductByHandle, getProductRecommendations } from '~/lib/shopify/api';
import type { Product } from '~/lib/shopify/types';
import {
  generateProductPageStructuredData,
  injectStructuredData,
  getProductFAQs,
} from '~/lib/seo/structured-data';
import { trackViewItem } from '~/lib/tracking/gtm';
import { generateProductMetaTags, generateProductOGTags } from '~/lib/seo/meta-tags';

type FeatureItem = { label: string; value: string } | string;

/**
 * Parse product features from custom.features metafield.
 * Supports JSON array of strings or array of { label, value } objects.
 */
function getProductFeatures(product: Product): FeatureItem[] {
  const mf = product.metafields?.find((m) => m?.namespace === 'custom' && m?.key === 'features');
  if (!mf?.value) return [];
  try {
    const parsed = JSON.parse(mf.value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item: unknown): item is FeatureItem =>
        typeof item === 'string' ||
        (typeof item === 'object' && item !== null &&
          typeof (item as any).label === 'string' &&
          typeof (item as any).value === 'string')
    );
  } catch {
    return [];
  }
}

/** User-facing tags worth surfacing as highlights */
const HIGHLIGHT_TAGS = new Set([
  'wireless', 'bluetooth', 'anc', 'active noise cancellation', 'noise cancelling',
  'usb-c', 'usb-a', 'nfc', 'dect', 'mono', 'stereo', 'duo',
  'uc', 'unified communications', 'microsoft teams', 'teams certified',
  'zoom certified', 'zoom', 'ems', 'ehs',
]);

/** Generate fallback highlights from product data when no features metafield exists */
function getFallbackHighlights(product: Product): string[] {
  const highlights: string[] = [];

  if (product.vendor) {
    highlights.push(`By ${product.vendor}`);
  }

  // Condition from metafield
  const conditionMf = product.metafields?.find(
    (m) => m?.namespace === 'mm-google-shopping' && m?.key === 'condition'
  );
  if (conditionMf?.value) {
    highlights.push(conditionMf.value === 'new' ? 'Brand New' : conditionMf.value);
  }

  // Free delivery threshold
  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  if (price >= 50) {
    highlights.push('Free UK Delivery');
  }

  // Surface user-facing tags
  for (const tag of product.tags) {
    if (HIGHLIGHT_TAGS.has(tag.toLowerCase())) {
      highlights.push(tag);
    }
  }

  if (product.availableForSale) {
    highlights.push('In Stock & Ready to Ship');
  }

  return highlights;
}

interface ProductPageProps {
  handle: string;
}

export function ProductPage({ handle }: ProductPageProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch product data
  useEffect(() => {
    async function fetchProduct() {
      setIsLoading(true);
      setError(null);

      try {
        const productData = await getProductByHandle(handle);

        if (!productData) {
          setError('Product not found');
          return;
        }

        setProduct(productData);
        trackViewItem(productData);

        // Fetch recommendations
        const recs = await getProductRecommendations(productData.id);
        setRecommendations(recs.slice(0, 4));
      } catch (err) {
        console.error('Failed to fetch product:', err);
        setError('Failed to load product');
      } finally {
        setIsLoading(false);
      }
    }

    fetchProduct();
  }, [handle]);

  const faqs = useMemo(() => (product ? getProductFAQs(product) : []), [product]);
  const features = useMemo(() => {
    if (!product) return [];
    const parsed = getProductFeatures(product);
    return parsed.length > 0 ? parsed : getFallbackHighlights(product);
  }, [product]);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // Update SEO
  useEffect(() => {
    if (!product) return;

    const meta = generateProductMetaTags(product);
    document.title = meta.title;

    // Update meta description
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement('meta');
      descMeta.setAttribute('name', 'description');
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute('content', meta.description);

    // Update OG tags
    const ogTags = generateProductOGTags(product);
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Inject structured data
    const breadcrumbs = [
      { name: 'Home', url: '/' },
      { name: 'Products', url: '/collections/all-products' },
      { name: product.title, url: `/products/${product.handle}` },
    ];
    const structuredData = generateProductPageStructuredData(product, breadcrumbs);
    const scriptId = 'product-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, [product]);

  if (isLoading) {
    return (
      <Layout>
        <div className="container-wide py-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Image Skeleton */}
            <div className="aspect-square bg-surface-coal rounded-2xl skeleton" />

            {/* Details Skeleton */}
            <div className="space-y-6">
              <div className="h-6 w-24 bg-surface-slate skeleton rounded" />
              <div className="h-10 w-3/4 bg-surface-slate skeleton rounded" />
              <div className="h-8 w-32 bg-surface-slate skeleton rounded" />
              <div className="h-12 w-full bg-surface-slate skeleton rounded" />
              <div className="h-64 w-full bg-surface-slate skeleton rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div className="container-wide py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-surface-coal rounded-2xl flex items-center justify-center">
            <svg className="w-10 h-10 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-accent-white mb-4">
            {error || 'Product Not Found'}
          </h1>
          <p className="text-accent-silver mb-8">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <a href="/collections/all-products" className="btn-primary">
            Browse All Products
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-wide py-8 lg:py-12">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm flex-wrap">
            <li>
              <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                Home
              </a>
            </li>
            <li className="text-accent-silver">/</li>
            <li>
              <a href="/collections/all-products" className="text-accent-silver hover:text-accent-white transition-colors">
                Products
              </a>
            </li>
            <li className="text-accent-silver">/</li>
            <li>
              <a
                href={`/collections/${product.vendor.toLowerCase()}`}
                className="text-accent-silver hover:text-accent-white transition-colors"
              >
                {product.vendor}
              </a>
            </li>
            <li className="text-accent-silver">/</li>
            <li className="text-accent-white truncate max-w-[200px]">{product.title}</li>
          </ol>
        </nav>

        {/* Product Details */}
        <ProductDetails product={product} />

        {/* FAQs & Product Highlights */}
        {(faqs.length > 0 || features.length > 0) && (
          <section className="mt-16 pt-12 border-t border-surface-slate">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* FAQs column */}
              {faqs.length > 0 && (
                <div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-8"
                  >
                    <span className="text-2xs font-mono uppercase tracking-widest text-brand-orange mb-2 block">
                      Common Questions
                    </span>
                    <h2 className="text-2xl font-display font-bold text-accent-white">
                      Frequently Asked Questions
                    </h2>
                  </motion.div>

                  <div className="space-y-3">
                    {faqs.map((faq, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="border border-surface-slate/30 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-graphite/30 transition-colors"
                        >
                          <span className="text-accent-white font-medium pr-4">{faq.question}</span>
                          <motion.svg
                            animate={{ rotate: openFaqIndex === index ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="w-5 h-5 text-accent-silver flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </motion.svg>
                        </button>
                        <AnimatePresence>
                          {openFaqIndex === index && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="px-5 pb-4 text-accent-silver leading-relaxed">
                                {faq.answer}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Highlights column */}
              {features.length > 0 && (
                <div className={`lg:sticky lg:top-8 lg:self-start${faqs.length === 0 ? ' lg:col-span-2 max-w-lg' : ''}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-8"
                  >
                    <span className="text-2xs font-mono uppercase tracking-widest text-brand-orange mb-2 block">
                      At a Glance
                    </span>
                    <h2 className="text-2xl font-display font-bold text-accent-white">
                      Product Highlights
                    </h2>
                  </motion.div>

                  <div className="bg-surface-coal rounded-2xl border border-surface-slate p-6 space-y-4">
                    {features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.06 }}
                        className="flex items-start gap-3"
                      >
                        <svg
                          className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {typeof feature === 'string' ? (
                          <span className="text-accent-silver leading-relaxed">{feature}</span>
                        ) : (
                          <div>
                            <span className="text-accent-white font-medium">{feature.label}</span>
                            <span className="text-accent-silver ml-1.5">— {feature.value}</span>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <section className="mt-20 pt-12 border-t border-surface-slate">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-end justify-between mb-8"
            >
              <div>
                <span className="text-2xs font-mono uppercase tracking-widest text-brand-orange mb-2 block">
                  You May Also Like
                </span>
                <h2 className="text-2xl font-display font-bold text-accent-white">
                  Related Products
                </h2>
              </div>
              <a href="/collections/all-products" className="btn-ghost text-sm hidden sm:flex">
                View All
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </motion.div>

            <ProductGrid products={recommendations} columns={4} listName="Recommendations" />
          </section>
        )}
      </div>
    </Layout>
  );
}
